import { clubApi } from "@/services/axios";
import { AxiosError } from "axios";
import { v4 as uuidv4 } from "uuid";

// «امور مالی» (api/Finance). Amounts are whole rials as strings; the salon comes from the token.

export type Capability =
  | "finance.view" | "expenses.write" | "sales.write" | "inventory.write" | "payroll.view" | "payroll.write"
  | "finance.settle" | "finance.close" | "finance.reopen" | "finance.export" | "finance.audit" | "finance.payslip.self";

export type Page<T> = { items: T[]; nextCursor?: string | null; totalCount: number; totals?: { amountRial?: string | null } | null; asOf: string; currency: string };
export type ListQuery = { from?: string; to?: string; limit?: number; cursor?: string | null };
export type IdResponse = { id: number; amountRial?: string | null };

export type Account = { id: number; name: string; kind: "Cash" | "Bank" | "Clearing"; active: boolean; balanceRial: string };
export type Terminal = { id: number; name: string; code: string; destinationAccountId: number; clearingAccountId: number };
export type Overview = { salesRial: string; receiptsRial: string; expensesRial: string; cashRial: string; bankRial: string; clearingRial: string; receivableRial: string; asOf: string };
export type Category = { code: string; name: string };
export type ExpenseStatus = "Unpaid" | "Paid" | "Reversed";
export type Expense = { id: number; title: string; categoryCode: string; amountRial: string; businessDate: string; dueDate?: string | null; status: ExpenseStatus; note?: string | null };
export type Product = { id: number; code: string; name: string; priceRial: string; quantityOnHand: number; lowStockThreshold: number; active: boolean; version: number };
export type StockMovement = { id: number; productId: number; kind: "Opening" | "Purchase" | "Sale" | "ReturnHealthy" | "ReturnDefective"; quantity: number; unitCostRial: string; businessDate: string };
export type ReportKind = "cash-flow" | "sales" | "receivables";
export type ReportRow = { id: number; businessDate: string; source: string; memo?: string | null; amountRial: string };
export type DailyPreview = { date: string; expectedCashRial: string; terminals: { terminalId: number; name: string; expectedRial: string }[]; closed: boolean; needsReview: boolean };
export type PayrollPeriod = { id: number; start: string; endExclusive: string; version: number };
export type PayrollStaff = { merchantUserId: number; name: string; fixedRial: string; shareRial: string; bonusRial: string; advanceRial: string; staffInsuranceRial: string; salonInsuranceRial: string; otherRial: string; netRial: string; status: string; version: number };
export type Advance = { id: number; merchantUserId: number; amountRial: string; remainingRial: string; businessDate: string };
export type Insurance = { id: number; periodId: number; merchantUserId: number; staffShareRial: string; salonShareRial: string; remainingRial: string };
export type PayrollPreview = { previewToken: string; expiresAtUtc: string; lines: { merchantUserId: number; netRial: string; shareRial: string; advanceRial: string }[] };
export type ExportJob = { id: number; status: string; fileName?: string | null; asOf?: string | null; content?: string | null };

export type FinanceError = { message?: string; code?: string; currentVersion?: string | null; status?: number };

const http = clubApi.instance;
const base = "/api/Finance";
const get = async <T>(path: string, params?: object) => (await http.get<T>(`${base}${path}`, { params })).data;

export const newKey = () => uuidv4();

// Every write carries an Idempotency-Key. If the answer is lost on the way back, the stored result of
// the same key is read instead of sending the request again.
async function write<T>(method: "post" | "patch", path: string, body: unknown, key: string): Promise<T> {
  try {
    return (await http.request<T>({ method, url: `${base}${path}`, data: body, headers: { "Idempotency-Key": key } })).data;
  } catch (error) {
    if ((error as AxiosError).response) throw error;
    for (let attempt = 0; attempt < 3; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
      try {
        const op = await get<{ status: string; responseJson?: string | null }>(`/operations/${encodeURIComponent(key)}`);
        if (op.status === "Completed" && op.responseJson) return JSON.parse(op.responseJson) as T;
      } catch (lookup) {
        if ((lookup as AxiosError).response?.status === 404) break;
      }
    }
    throw error;
  }
}

export function financeError(error: unknown): FinanceError {
  const response = (error as AxiosError<FinanceError>)?.response;
  return { ...(response?.data ?? {}), status: response?.status };
}

export const finance = {
  capabilities: () => get<{ capabilities: Capability[]; merchantUserId?: number | null }>("/capabilities"),
  accounts: () => get<Account[]>("/accounts"),
  createAccount: (body: { name: string; kind: "cash" | "bank" }, key: string) => write<IdResponse>("post", "/accounts", body, key),
  terminals: () => get<Terminal[]>("/terminals"),
  createTerminal: (body: { name: string; code: string; destinationAccountId: number }, key: string) => write<IdResponse>("post", "/terminals", body, key),
  opening: (body: { accountId: number; amountRial: string; businessDate: string; reference: string }, key: string) => write<IdResponse>("post", "/opening-balances", body, key),
  overview: (from: string, to: string) => get<Overview>("/overview", { from, to }),

  categories: () => get<Category[]>("/expense-categories"),
  expenses: (query: ListQuery) => get<Page<Expense>>("/expenses", query),
  createExpense: (body: object, key: string) => write<IdResponse>("post", "/expenses", body, key),
  payExpense: (id: number, body: { accountId: number; paidAt: string; reference?: string }, key: string) => write<IdResponse>("post", `/expenses/${id}/payments`, body, key),
  reverseExpense: (id: number, body: { effect: "accrual" | "nocash" | "cashreturn"; reason: string; accountId?: number | null }, key: string) => write<IdResponse>("post", `/expenses/${id}/reversals`, body, key),
  uploadAttachment: async (file: File, key: string) => {
    const data = new FormData();
    data.append("file", file);
    return write<{ id: number }>("post", "/attachments", data, key);
  },
  attachmentUrl: async (id: number) => {
    const ticket = await get<{ token: string }>(`/attachments/${id}/download`);
    return `${http.defaults.baseURL ?? ""}${base}/attachments/file/${encodeURIComponent(ticket.token)}`;
  },

  products: (query: ListQuery = {}) => get<Page<Product>>("/products", { limit: 100, ...query }),
  createProduct: (body: object, key: string) => write<IdResponse>("post", "/products", body, key),
  updateProduct: (id: number, body: object, key: string) => write<IdResponse>("patch", `/products/${id}`, body, key),
  stockPurchase: (body: object, key: string) => write<IdResponse>("post", "/stock-purchases", body, key),
  movements: (query: ListQuery) => get<Page<StockMovement>>("/stock-movements", query),
  createSale: (body: object, key: string) => write<IdResponse>("post", "/sales", body, key),
  addReceipt: (id: number, body: object, key: string) => write<IdResponse>("post", `/sales/${id}/receipts`, body, key),
  refundSale: (id: number, body: object, key: string) => write<IdResponse>("post", `/sales/${id}/refunds`, body, key),

  clearing: () => get<Account[]>("/clearing-balances"),
  settle: (body: object, key: string) => write<IdResponse>("post", "/clearing-settlements", body, key),
  report: (kind: ReportKind, query: ListQuery) => get<Page<ReportRow>>(`/reports/${kind}`, query),
  dailyPreview: (date: string) => get<DailyPreview>("/daily-closes/preview", { date }),
  closeDay: (body: { businessDate: string; actualCashRial: string; reason?: string }, key: string) => write<IdResponse>("post", "/daily-closes", body, key),

  periods: () => get<PayrollPeriod[]>("/payroll-periods"),
  createPeriod: (body: { persianYear: number; persianMonth: number }, key: string) => write<IdResponse>("post", "/payroll-periods", body, key),
  staff: (periodId: number) => get<PayrollStaff[]>(`/payroll-periods/${periodId}/staff`),
  adjustStaff: (periodId: number, staffId: number, body: object, key: string) => write<IdResponse>("patch", `/payroll-periods/${periodId}/staff/${staffId}`, body, key),
  advances: () => get<Advance[]>("/advances"),
  createAdvance: (body: object, key: string) => write<IdResponse>("post", "/advances", body, key),
  allocateAdvance: (id: number, body: { periodId: number; amountRial: string }, key: string) => write<IdResponse>("post", `/advances/${id}/allocations`, body, key),
  previewPayroll: (body: { periodId: number; employeeIds: number[] }, key: string) => write<PayrollPreview>("post", "/payroll-batches/preview", body, key),
  commitPayroll: (body: object, key: string) => write<IdResponse>("post", "/payroll-batches", body, key),
  insurance: () => get<Insurance[]>("/insurance-liabilities"),
  payInsurance: (body: object, key: string) => write<IdResponse>("post", "/insurance-payments", body, key),

  createExport: (body: { kind: string; from: string; to: string }, key: string) => write<IdResponse>("post", "/exports", body, key),
  exportJob: (id: number) => get<ExportJob>(`/exports/${id}`),
};

export const financeKeys = { all: ["finance"] as const };
